/**
 * [C] 세차요금 기록 — 외주(gtli) 관리자 "세차요금 기록"(결제 건별) 재현.
 *
 * 컬럼(벤더 순서): 일련번호·차량번호·입차시각·결제시각·결제금액·승인번호·카드사·장소·결제방법·상품명·하부세차·메모.
 * 정렬은 결제시각 내림차순(백엔드) · 같은 번호판 10초내 중복은 백엔드에서 제거 · 마지막 합계행.
 * 데이터: GET /owner-console/modernwash/vendor-parity/payments (live read-only, 외주 admin id/pw 필요).
 *
 * ⚠️ 운영 승인(1회 예외): 승인번호·카드사 등 결제 민감 컬럼 노출. 접근은 백엔드가 master/연동매장(jeonju) 매니저로 강제.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import QueryError from '../../components/QueryError';
import { getAdminRole } from '../../utils/auth';
import { formatWon } from '../../owner-console-v2/modules/carwash/modernwash/format';
import { getVendorPayments } from '../../api/modernwashVendor';
import {
  surfaceSx,
  kstDate,
  formatVendorDateTime,
  SourceBanner,
  shouldRenderBody,
  useMasterStores,
  DateField,
  StoreSelect,
} from './vendorParityCommon';

const VendorPaymentListPage = () => {
  const navigate = useNavigate();
  const role = getAdminRole();
  const isMaster = role === 'master';
  const today = kstDate(0);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const rangeInvalid = startDate > endDate;

  const stores = useMasterStores(isMaster);
  const [storeId, setStoreId] = useState('');
  const storeParam = isMaster ? storeId || undefined : undefined;

  const [state, setState] = useState({ loading: true, error: null, res: null });
  const load = useCallback(() => {
    if (rangeInvalid) return undefined;
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    getVendorPayments({ storeId: storeParam, start: startDate, end: endDate })
      .then((res) => alive && setState({ loading: false, error: null, res }))
      .catch((e) => alive && setState({ loading: false, error: e, res: null }));
    return () => {
      alive = false;
    };
  }, [storeParam, startDate, endDate, rangeInvalid]);
  useEffect(() => load(), [load]);

  const res = state.res;
  const items = res?.items || [];
  const totalPaidfee = res?.data?.totalPaidfee ?? 0;
  const showBody = shouldRenderBody(res) && !state.loading && !state.error;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => navigate('/modernwash')} aria-label="ModernWash 운영으로">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 20, md: 28 }, fontWeight: 800, letterSpacing: '-0.03em' }}>
            세차요금 기록
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            외주 관리자 결제 건별 내역 · 재무 원장 아님(운영 참고)
          </Typography>
        </Box>
        <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            label={isMaster ? '전체 매장 조회 (master)' : '본인 매장 고정 (manager)'}
            variant="outlined"
            sx={{ height: 30, borderRadius: 999, fontWeight: 700 }}
          />
          <IconButton size="small" onClick={load} aria-label="새로고침">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <SourceBanner res={res} />
      </Box>

      <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.75, md: 2 }, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateField label="시작일" value={startDate} max={endDate} onChange={setStartDate} />
          <DateField label="종료일" value={endDate} min={startDate} max={today} onChange={setEndDate} />
          {isMaster ? <StoreSelect stores={stores} value={storeId} onChange={setStoreId} /> : null}
        </Box>
        {rangeInvalid ? (
          <Typography sx={{ fontSize: 12.5, color: 'error.main', mt: 1 }}>시작일이 종료일보다 늦습니다.</Typography>
        ) : null}
      </Paper>

      {state.error ? (
        <QueryError error={state.error} onRetry={load} context="세차요금 기록" />
      ) : state.loading ? (
        <LinearProgress sx={{ mb: 2 }} />
      ) : null}

      {showBody ? (
        <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.5, md: 2 } }}>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1.5 }}>
            총 {items.length.toLocaleString()}건 · 결제시각 최신순
          </Typography>
          {items.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>기간 내 결제 내역이 없습니다.</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 620 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="right">일련번호</TableCell>
                    <TableCell>차량번호</TableCell>
                    <TableCell>입차시각</TableCell>
                    <TableCell>결제시각</TableCell>
                    <TableCell align="right">결제금액</TableCell>
                    <TableCell>승인번호</TableCell>
                    <TableCell>카드사</TableCell>
                    <TableCell>장소</TableCell>
                    <TableCell>결제방법</TableCell>
                    <TableCell>상품명</TableCell>
                    <TableCell align="center">하부세차</TableCell>
                    <TableCell>메모</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row, i) => (
                    <TableRow key={`${row.approvno || ''}-${row.paymentTime || ''}-${i}`} hover>
                      <TableCell align="right">{i + 1}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{row.licensePlate}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {row.loopEventTime ? formatVendorDateTime(row.loopEventTime) : ''}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatVendorDateTime(row.paymentTime)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                        {formatWon(row.paidfee)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.approvno || ''}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.issuer || ''}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.location || ''}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.howToPay || ''}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.productionName || ''}</TableCell>
                      <TableCell align="center">{row.underbodyWash || ''}</TableCell>
                      <TableCell>{row.contents || ''}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell />
                    <TableCell sx={{ fontWeight: 800 }}>합계</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                      {formatWon(totalPaidfee)}
                    </TableCell>
                    <TableCell colSpan={7} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      ) : null}
    </Box>
  );
};

export default VendorPaymentListPage;
